import React from 'react';
import { DealDetail, DealProduct, DealStage } from '../types';

export const DEAL_STAGES: DealStage[] = [
  'Qualification',
  'Needs Analysis',
  'Value Proposition',
  'Identify Decision Makers',
  'Proposal/Price Quote',
  'Negotiation/Review',
  'Closed Won',
  'Closed Lost',
  'Closed Lost to Competition',
];

// Per-stage color map: [bg, text, border]
export const STAGE_COLORS: Record<DealStage, { bg: string; color: string; border: string }> = {
  'Qualification':               { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  'Needs Analysis':              { bg: '#fefce8', color: '#a16207', border: '#fde68a' },
  'Value Proposition':           { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  'Identify Decision Makers':    { bg: '#fdf4ff', color: '#7e22ce', border: '#e9d5ff' },
  'Proposal/Price Quote':        { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },  // reused below for won too
  'Negotiation/Review':          { bg: '#eff6ff', color: '#1e40af', border: '#93c5fd' },
  'Closed Won':                  { bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
  'Closed Lost':                 { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
  'Closed Lost to Competition':  { bg: '#fff1f2', color: '#9f1239', border: '#fda4af' },
};

/** Returns Tailwind classes for a pill/badge (legacy, kept for compat) */
export const getStageBadgeClass = (stage: DealStage): string => {
  if (stage === 'Closed Won') return 'bg-green-600 text-white';
  if (stage === 'Closed Lost' || stage === 'Closed Lost to Competition') return 'bg-red-600 text-white';
  if (stage === 'Negotiation/Review') return 'bg-blue-600 text-white';
  return 'bg-yellow-500 text-white';
};

/** Inline style object for a square stage badge */
export const getStageBadgeStyle = (stage: DealStage): React.CSSProperties => {
  const c = STAGE_COLORS[stage] ?? { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    background: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    whiteSpace: 'nowrap',
  };
};

export const calculateProducts = (deal: DealDetail): DealProduct[] => {
  const products: DealProduct[] = [];

  // Handle both camelCase and snake_case (Laravel returns snake_case)
  const projectRequest = deal.projectRequest || (deal as any).project_request;

  if (!projectRequest || !deal.customer) {
    return products;
  }

  const workRequest = projectRequest.work_request || [];
  const impactedParcels = Math.round(projectRequest.impacted_parcels || 1);
  const qtyVal = impactedParcels > 0 ? impactedParcels : 1;

  // Get ClientID from customer
  const clientID = deal.customer.client_id || deal.customer.short_name || '';

  // Research logic
  if (workRequest.includes('Research')) {
    const researchGovtOwned = projectRequest.research_is_government_owned === true;
    const unitPrice = Math.round(researchGovtOwned ? 11 : 8);

    products.push({
      itemName: `Title Research Agent - Research - ${clientID}`,
      unit: unitPrice,
      quantity: qtyVal,
      total: Math.round(unitPrice * qtyVal),
    });

    products.push({
      itemName: `Project Manager - Research - ${clientID}`,
      unit: 3,
      quantity: 1,
      total: 3,
    });
  }

  // Acquisition logic
  if (workRequest.includes('Acquisition')) {
    const acquisitionGovtOwned = projectRequest.acquisition_is_government_owned === true;
    const unitPrice = Math.round(acquisitionGovtOwned ? 35 : 30);

    products.push({
      itemName: `ROW Agent - Acquisition - ${clientID}`,
      unit: unitPrice,
      quantity: 1,
      total: unitPrice,
    });

    products.push({
      itemName: `Project Manager - Acquisition - ${clientID}`,
      unit: 3,
      quantity: 1,
      total: 3,
    });
  }

  // QA/QC logic
  if (workRequest.includes('QA/QC')) {
    const necessaryDocumentation = projectRequest.qa_has_documentation === true;
    const unitPrice = Math.round(necessaryDocumentation ? 3 : 6);

    products.push({
      itemName: `ROW Agent - QA/QC - ${clientID}`,
      unit: unitPrice,
      quantity: 1,
      total: unitPrice,
    });

    products.push({
      itemName: `Project Manager - QA/QC - ${clientID}`,
      unit: 3,
      quantity: 1,
      total: 3,
    });
  }

  // Basic Request logic
  if (workRequest.includes('Basic Request')) {
    products.push({
      itemName: `Title Research Agent - Basic Request - ${clientID}`,
      unit: 3,
      quantity: 1,
      total: 3,
    });

    products.push({
      itemName: `Project Manager - Basic Request - ${clientID}`,
      unit: 3,
      quantity: 1,
      total: 3,
    });
  }

  return products;
};
